<template>
  <v-card
    :class="cardClass"
    tile
  >
    <v-card
      class="bb-0"
      border
      tile
    >
      <v-card-title role="heading">
        {{ $bct('Staff') }}

        <v-row
          class="d-flex align-center"
          dense
        >
          <LabeledAutocomplete
            v-if="providers.length > 1"
            v-model="selectedProviderId"
            :cols="3"
            :disabled="processing"
            :items="providers"
            :placeholder="$bct('Select provider')"
            class="pa-0"
            item-title="name"
            item-value="id"
            clearable
            dense
          />

          <LabeledTextfield
            v-model="search"
            :cols="3"
            :placeholder="$bct('Search')"
            class="mt-1"
            prepend-inner-icon="search"
            clearable
            dense
          />

          <v-spacer />

          <StandardButton
            @click="newStaffMember()"
            class="mt-1"
            data-cy="new_staff_member"
            role="create"
            text="New staff member"
          />
        </v-row>
      </v-card-title>
    </v-card>

    <v-card-text class="pa-0">
      <DataTable
        v-model="selectedMember"
        @click:row="editStaffMember"
        :headers="tableHeaders"
        :items="employees"
        :loading="processing"
        :search="search"
        class="b-radius-0 b-1 bc-outlined-gray"
        height="70vh"
        item-key="id"
        items-per-page="-1"
        fixed-header
      >
        <template #[`item.name`]="{ item }">
          <div v-if="item.employment_role == 'Admin'">
            <v-icon
              icon="admin_panel_settings"
              size="small"
            />
            {{ item.name }}
          </div>

          <div v-else>
            <v-icon
              icon="person"
              size="small"
            />
            {{ item.name }}
          </div>
        </template>

        <template #[`item.title`]="{ item }">
          {{ item.employments?.[0]?.title }}
        </template>

        <template #[`item.locations`]="{ item }">
          <v-chip
            v-if="item.provider_ids.length === 0"
            size="x-small"
            variant="outlined"
          >
            {{ $bct('All locations') }}
          </v-chip>

          <v-chip-group
            column
            mandatory
          >
            <template v-for="p in providers">
              <v-chip
                v-if="item.provider_ids.includes(p.id)"
                :key="p.id"
                :text="p.name"
                :value="p.id"
                size="x-small"
                variant="outlined"
              />
            </template>
          </v-chip-group>
        </template>

        <template #[`item.background_check_status`]="{ item }">
          <span v-if="item.background_check_status == 'Clear'">
            <v-icon
              icon="verified_user"
              size="small"
            />
            {{ $bct('Clear') }}
          </span>

          <span v-else>
            <v-icon
              icon="check_circle"
              size="small"
            />
            {{ $bct('Enrolled') }}
          </span>
        </template>

        <template #[`item.employment_status`]="{ item }">
          <v-icon
            class="me-1"
            size="small"
          >
            {{
              item.employment_status == 'Dismissed' || item.deactivated ? 'person_off' : 'person'
            }}
          </v-icon>
          {{ item.deactivated ? $bct('Deactivated') : $bct(item.employment_status) }}
        </template>

        <template #[`item.start_date`]="{ item }">
          <LongDate
            :date="item.employments?.[0]?.start_date || item.created_at"
            short-month
          />
        </template>

        <template #[`item.updated_at`]="{ item }">
          <LongDate
            :date="item.updated_at"
            short-month
          />
        </template>

        <!-- <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end align-center">
            <ActionMenu
              @click:action:dismiss="dismiss(item)"
              @click:action:reactivate="reactivateEmployee(item)"
              :items="getEmployeeActions(item)"
              button-icon="more_vert"
            />
          </div>
        </template> -->

        <template #no-data>
          <NullState class="my-4" />
        </template>

        <template #bottom />
      </DataTable>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { useRouter } from 'vue-router';

// import ActionMenu from '@/shared/components/ActionMenu.vue';
import DataTable from '@/shared/components/table/DataTable.vue';
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import NullState from '@/shared/components/NullState.vue';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';

const router = useRouter();
const { scopedApi } = useApi();
const { isSpecialist } = useRole();
const $bct = inject('translator');

const props = defineProps({
  cardClass: {
    type: String,
    default: 'ma-4',
  },
  initialProvider: {
    type: Object,
    default: undefined,
  },
});

const employees = ref([]);
const processing = ref(false);
const providers = ref([]);
const search = ref(null);
const selectedMember = ref({});
const selectedProviderId = ref(null);
const tableHeaders = ref([
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Job title',
    key: 'title',
  },
  ...(providers.value.length > 1
    ? [
        {
          title: 'Locations',
          key: 'locations',
          maxWidth: '200px',
        },
      ]
    : []),
  {
    title: 'Employment status',
    key: 'employment_status',
  },
  {
    title: 'Start date',
    key: 'start_date',
    align: 'center',
  },
  {
    title: '',
    value: 'actions',
    align: 'right',
  },
]);

if (props.initialProvider) {
  selectedProviderId.value = props.initialProvider.id;
}

watch(selectedProviderId, async () => {
  processing.value = true;
  await load();
});

async function load() {
  let params = {
    provider_id: props.initialProvider?.id || selectedProviderId.value,
    // These are ignored by the manager API
    is_provider: true,
    include_deactivated: true,
  };

  if (props.initialProvider) {
    providers.value = [props.initialProvider];
  } else {
    const providerResp = await scopedApi.provider.index();
    if (providerResp?.status !== 200) return;

    providers.value = providerResp.data;
  }
  const staffResp = await scopedApi.member.index(params);
  if (staffResp?.status !== 200) return;

  employees.value = staffResp.data.map((employee) => ({
    ...employee,
    background_check_status: employee.background_check?.status || '',
    employment_role: employee.employments?.[0]?.business_role || 'Member',
    employment_status: employee.employments?.[0]?.end_date ? 'Dismissed' : 'Active',
  }));

  processing.value = false;
}

function staffShowRoute(memberId) {
  const resolvedProviderId = props.initialProvider?.id || selectedProviderId.value || undefined;

  if (isSpecialist && props.initialProvider) {
    return {
      name: 'ProviderStaffShow',
      params: {
        providerId: props.initialProvider.id,
        memberId,
      },
    };
  }

  return {
    name: 'ProviderStaffShow',
    params: { memberId },
    query: resolvedProviderId ? { providerId: resolvedProviderId } : {},
  };
}

function newStaffMember() {
  void router.push(staffShowRoute('new'));
}

function editStaffMember(_, { item, _row }) {
  void router.push(staffShowRoute(item.id));
}

// TODO: Bring this back when there is time to get it all working properly
// function getEmployeeActions(employee) {
//   if (employee.end_date) {
//     return [{ title: 'Reactivate', event: 'reactivate' }];
//   } else {
//     return [{ title: 'Dismiss', event: 'dismiss' }];
//   }
// }

onMounted(load);
</script>
